import {
  Banknote,
  Briefcase,
  ClipboardList,
  Contact,
  FileSignature,
  FileText,
  type LucideIcon,
  Package,
  Receipt,
  Truck,
} from 'lucide-react';

import type { AccessRule } from '../../auth/useAccess.js';
import type { SearchHit, SearchType } from './contractsMirror.js';

/**
 * What the palette needs to know about a record type that the endpoint does not
 * send: the mark it wears, the word it is called, the screen it opens, and the
 * rule that decides whether this session may see the group at all.
 *
 * THE ACCESS COLUMN IS A SECOND GATE, NOT THE FIRST. The api worker already
 * filters every hit by the same rule its own list route applies, so a type this
 * user cannot reach never appears in `types` and returns no rows. This table
 * mirrors that server table so the two can be read side by side, and so the
 * stricter of the two always wins — a server that ever widened a gate could not
 * widen the palette with it. `searchCatalog.test.ts` pins the pair against the
 * mapping documented in the service.
 *
 * The icon and the label are deliberately the ones that type's own destination
 * already wears in `navData.ts`, so a job row in the palette and the Jobs tab in
 * the bar read as the same thing. Expenses are the one type with no destination
 * of their own, so they carry the only mark and label declared here.
 */
export interface SearchTypeEntry {
  icon: LucideIcon;
  /** i18n key. Reuses the nav label wherever the type has a destination. */
  labelKey: string;
  access: AccessRule;
  /** Where a hit of this type opens. */
  path: (hit: SearchHit) => string;
}

export const SEARCH_CATALOG: Record<SearchType, SearchTypeEntry> = {
  // GET /customers — requireManagerOrAdmin
  client: {
    icon: Contact,
    labelKey: 'nav:items.clients',
    access: { minRole: 'Manager' },
    path: (hit) => `/clients/${hit.id}`,
  },
  // GET /jobs — jobs.view
  job: {
    icon: Briefcase,
    labelKey: 'nav:items.jobs',
    access: { permission: 'jobs.view' },
    path: (hit) => `/jobs/${hit.id}`,
  },
  // GET /invoices — requireManagerOrAdmin
  invoice: {
    icon: FileText,
    labelKey: 'nav:items.invoices',
    access: { minRole: 'Manager' },
    path: (hit) => `/invoices/${hit.id}`,
  },
  // GET /estimates — requireManagerOrAdmin
  estimate: {
    icon: ClipboardList,
    labelKey: 'nav:items.estimates',
    access: { minRole: 'Manager' },
    path: (hit) => `/estimates/${hit.id}`,
  },
  // Expenses are job financials — financials.view.
  //
  // An expense has no page of its own: it is read on the job that owns it, which
  // is why the endpoint sends `jobId`. The column is nullable in the schema and
  // the writer has always set it, so a null can only come from imported history.
  //
  // The check is on the TYPE, not on `=== null`, so the two repos can ship in
  // either order: a worker that predates the field sends no `jobId` at all, and
  // `undefined` has to land on the same fallback a null does or the row routes
  // to `/jobs/undefined`. Either way it is the jobs list — a real screen —
  // rather than a result nothing can open.
  expense: {
    icon: Banknote,
    labelKey: 'nav:search.types.expense',
    access: { permission: 'financials.view' },
    path: (hit) => (typeof hit.jobId === 'number' ? `/jobs/${hit.jobId}` : '/jobs'),
  },
  // GET /monthly-bills — financials.view. The list is a single screen and there
  // is no detail route, so a bill opens the list it lives on.
  bill: {
    icon: Receipt,
    labelKey: 'nav:items.monthlyBills',
    access: { permission: 'financials.view' },
    path: () => '/monthly-bills',
  },
  // GET /inventory — inventory.view
  inventory: {
    icon: Package,
    labelKey: 'nav:items.inventory',
    access: { permission: 'inventory.view' },
    path: (hit) => `/inventory/${hit.id}`,
  },
  // GET /fleet — fleet.view
  fleet: {
    icon: Truck,
    labelKey: 'nav:items.fleet',
    access: { permission: 'fleet.view' },
    path: (hit) => `/fleet/${hit.id}`,
  },
  // GET /service-agreements — invoices.view
  agreement: {
    icon: FileSignature,
    labelKey: 'nav:items.serviceAgreements',
    access: { permission: 'invoices.view' },
    path: (hit) => `/service-agreements/${hit.id}`,
  },
};
